// Reedle Subscription Tier Configuration
// Synced with PostgreSQL function: reedle_get_tier_config()

export const REEDLE_TIERS = {
  free: {
    displayName: 'Free',
    monthlyCredits: 80,
    priceMonthlyUsd: 0,
    priceAnnualUsd: 0,
    isVip: false,
    limits: {
      articleStorage: 250,
      lists: 5,
      pdfUploadsPerMonth: 10,
      flashcardDecks: 3,
      ragSearchesPerMonth: 5,
      ttsPerMonth: 3,
      sharedListsTotal: 3,
    },
    features: ['basic_ai_models', 'join_shared_lists'],
  },
  pro: {
    displayName: 'Pro',
    monthlyCredits: 400,
    priceMonthlyUsd: 4.99,
    priceAnnualUsd: 47.99,
    isVip: true,
    limits: {
      articleStorage: 500,
      lists: 20,
      pdfUploadsPerMonth: null, // unlimited
      flashcardDecks: 10,
      ragSearchesPerMonth: null, // unlimited
      ttsPerMonth: null, // unlimited
      sharedListsTotal: null, // unlimited
    },
    features: [
      'custom_fonts',
      'audio_tts',
      'mcp_api',
      'create_shared_lists',
      'advanced_ai_models',
    ],
  },
  max: {
    displayName: 'Max',
    monthlyCredits: 1200,
    priceMonthlyUsd: 14.99,
    priceAnnualUsd: 143.99,
    isVip: true,
    limits: {
      articleStorage: null, // unlimited
      lists: null,
      pdfUploadsPerMonth: null,
      flashcardDecks: null,
      ragSearchesPerMonth: null,
      ttsPerMonth: null,
      sharedListsTotal: null,
    },
    features: [
      'custom_fonts',
      'audio_tts',
      'mcp_api',
      'create_shared_lists',
      'advanced_ai_models',
      'priority_processing',
    ],
  },
} as const;

export type ReedleTier = keyof typeof REEDLE_TIERS;
export type ReedleTierConfig = (typeof REEDLE_TIERS)[ReedleTier];

export const TIER_ORDER: ReedleTier[] = ['free', 'pro', 'max'];

// VIP-only features (require Pro or Max) — binary gate: free=no, pro/max=yes
export const VIP_FEATURES = [
  'custom_fonts',
  'mcp_api',
  'advanced_ai_models',
] as const;

// Soft-gated features — shown to free users as grayed + star, usable by pro/max
// Subset of VIP_FEATURES used by UI to render upgrade prompts instead of hiding
export const SOFT_GATE_FEATURES = [
  'custom_fonts',
  'advanced_ai_models',
] as const;

// Hard-gated features — completely hidden/inaccessible for free users
export const HARD_GATE_FEATURES = ['mcp_api'] as const;

// Max-only features
export const MAX_ONLY_FEATURES = ['priority_processing'] as const;

// Features that free users have LIMITED (not zero) access to — use limits, not canAccessFeature
// These are NOT in VIP_FEATURES because free users CAN use them (within limits)
export const LIMITED_FEATURES = ['audio_tts', 'create_shared_lists'] as const;

export type VipFeature = (typeof VIP_FEATURES)[number];
export type SoftGateFeature = (typeof SOFT_GATE_FEATURES)[number];
export type HardGateFeature = (typeof HARD_GATE_FEATURES)[number];
export type MaxOnlyFeature = (typeof MAX_ONLY_FEATURES)[number];
export type LimitedFeature = (typeof LIMITED_FEATURES)[number];
export type ReedleFeature = VipFeature | MaxOnlyFeature;

// Limit types that correspond to monthly usage (reset each month)
export const MONTHLY_LIMITS = [
  'pdfUploadsPerMonth',
  'ragSearchesPerMonth',
  'ttsPerMonth',
] as const;

// Limit types that are total counts (not monthly)
export const TOTAL_LIMITS = [
  'articleStorage',
  'lists',
  'flashcardDecks',
  'sharedListsTotal',
] as const;

export type MonthlyLimitType = (typeof MONTHLY_LIMITS)[number];
export type TotalLimitType = (typeof TOTAL_LIMITS)[number];
export type LimitType = keyof ReedleTierConfig['limits'];

// Article storage soft warning threshold (show hint before hard limit)
export const ARTICLE_STORAGE_SOFT_WARNING = 200;

export function getTierCredits(tier: ReedleTier): number {
  return REEDLE_TIERS[tier].monthlyCredits;
}

/** Tier name as shown to the reader. An absent tier is the free plan. */
export function getTierDisplayName(tier: ReedleTier | null | undefined): string {
  return REEDLE_TIERS[tier as ReedleTier]?.displayName ?? REEDLE_TIERS.free.displayName;
}

export function isVipTier(tier: ReedleTier): boolean {
  return tier !== 'free';
}

export function getTierLimit(
  tier: ReedleTier,
  limitType: LimitType,
): number | null {
  return REEDLE_TIERS[tier].limits[limitType];
}

export function canAccessFeature(tier: ReedleTier, feature: ReedleFeature): boolean {
  if (MAX_ONLY_FEATURES.includes(feature as MaxOnlyFeature)) {
    return tier === 'max';
  }
  if (VIP_FEATURES.includes(feature as VipFeature)) {
    return tier === 'pro' || tier === 'max';
  }
  return true;
}

export function getRequiredTierForFeature(feature: ReedleFeature): ReedleTier {
  if (MAX_ONLY_FEATURES.includes(feature as MaxOnlyFeature)) {
    return 'max';
  }
  if (VIP_FEATURES.includes(feature as VipFeature)) {
    return 'pro';
  }
  return 'free';
}

/**
 * Check if a limit type is a monthly limit (resets each month)
 */
export function isMonthlyLimit(limitType: LimitType): boolean {
  return (MONTHLY_LIMITS as readonly string[]).includes(limitType);
}

/**
 * Check if a feature is soft-gated (shown but grayed out for free users)
 */
export function isSoftGateFeature(feature: string): boolean {
  return (SOFT_GATE_FEATURES as readonly string[]).includes(feature);
}
